import { useEffect, useRef, useState } from 'react';

export default function Benefits() {
  const items = [
    { n: '01', title: 'Lower monthly energy costs.', copy: 'Offset the majority of your electric bill from day one and lock in predictable rates for decades.' },
    { n: '02', title: 'Independence from the grid.', copy: 'Generate, store and use your own power — with resilience during outages and peak-rate hours.' },
    { n: '03', title: 'Long-term property value.', copy: 'Solar homes and commercial buildings routinely resell at a measurable premium over comparable properties.' },
    { n: '04', title: 'Cleaner air, quieter impact.', copy: 'Every system installed offsets tons of CO₂ over its lifetime — clean infrastructure that quietly works in the background.' },
  ];

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="py-[64px] md:py-[120px] bg-white">
      <div className="container-x">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7">
            <h2 className="display-font text-[34px] md:text-[56px] font-semibold leading-[1.02] text-solar-ink max-w-[720px]">
              Why the smart move is the sun.
            </h2>
          </div>
        </div>

        <div ref={gridRef} className="mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10 md:gap-y-14">
          {items.map((it, i) => (
            <div
              key={it.n}
              style={{
                transitionDelay: visible ? `${i * 90}ms` : '0ms',
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                opacity: visible ? 1 : 0,
              }}
              className="group relative pt-6 transition-all duration-[700ms] ease-out will-change-transform hover:-translate-y-[2px] cursor-default"
            >
              <span className="absolute inset-x-0 top-0 h-px bg-solar-divider" />
              <span className="absolute left-0 top-0 h-px w-0 bg-solar-ink/70 transition-all duration-500 ease-out group-hover:w-full" />

              <div className="flex items-baseline gap-4">
                <span className="text-xs font-medium tracking-[0.18em] text-solar-muted tabular-nums transition-colors duration-300 group-hover:text-solar-ink">
                  {it.n}
                </span>
                <h3 className="display-font text-[20px] md:text-[26px] font-semibold leading-tight text-solar-ink/90 transition-colors duration-300 group-hover:text-solar-ink">
                  {it.title}
                </h3>
              </div>
              <p className="mt-4 pl-8 text-[15px] md:text-base leading-relaxed text-solar-muted">
                {it.copy}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}